import * as React from "react";
import { useMachine } from "@xstate/react";
import { verifyAddress } from "@nju/data-access-portal-wwt";
import { addClientAddress } from "@nju/data-access-sales-client";
import {
  clearFormError,
  model,
  setApartmentNumber,
  setApartmentNumberError,
  setCity,
  setHouseNumber,
  setSkipAddress,
  setStreet,
  setWithoutStreet,
  validate,
} from "./model";

const createWWTMachine = ({
  onPositiveUpdate,
  onNegativeUpdate,
}: {
  onPositiveUpdate: () => void;
  onNegativeUpdate: () => void;
}) => {
  return model.createMachine(
    {
      initial: "ready",
      states: {
        ready: {
          on: {
            setCity: {
              actions: setCity,
            },
            setStreet: {
              actions: setStreet,
            },
            setWithoutStreet: {
              actions: setWithoutStreet,
            },
            setSkipAddress: {
              actions: setSkipAddress,
            },
            setHouseNumber: {
              actions: setHouseNumber,
            },
            setApartmentNumber: {
              actions: setApartmentNumber,
            },
            verify: {
              target: "validate",
            },
          },
        },
        validate: {
          entry: validate,
          always: [
            {
              target: "verify",
              cond: "hasNoErrors",
            },
            {
              target: "ready",
            },
          ],
        },
        verify: {
          entry: clearFormError,
          invoke: {
            src: "validateAddress",
          },
          on: {
            addressVerified: {
              target: "addAddress",
            },
            setApartmentNumberError: {
              actions: setApartmentNumberError,
              target: "ready",
            },
          },
        },
        addAddress: {
          invoke: {
            src: "addAddress",
          },
          on: {
            addressUpdateSuccess: {
              actions: onPositiveUpdate,
              target: "final",
            },
            addressUpdateFail: {
              actions: onNegativeUpdate,
              target: "error",
            },
          },
        },
        final: {
          type: "final",
        },
        error: {
          type: "final",
        },
      },
    },
    {
      services: {
        validateAddress: ({ city, street, houseNumber, apartmentNumber }) => {
          return async (callback) => {
            if (!city) {
              callback({ type: "missingCity" });
              return;
            }
            const result = await verifyAddress({
              cityId: city.id,
              cityName: city.name,
              // eslint-disable-next-line unicorn/no-null
              streetId: street?.id ?? null,
              // eslint-disable-next-line unicorn/no-null
              streetName: street?.name ?? null,
              houseNumber: houseNumber,
              apartmentNumber: apartmentNumber,
            });

            if (
              result.isError &&
              result.err.code === "MISSING_APARTMENT_NUMBER"
            ) {
              return callback({ type: "setApartmentNumberError" });
            }

            callback({
              type: "addressVerified",
            });
          };
        },
        addAddress: ({ city, street, houseNumber, apartmentNumber }) => {
          return async (callback) => {
            if (!city) {
              callback({ type: "missingCity" });
              return;
            }
            const result = await addClientAddress({
              municipality: city.name,
              // eslint-disable-next-line unicorn/no-null
              street: street?.name ?? null,
              streetNumber: houseNumber,
              apartmentNumber: apartmentNumber,
              // eslint-disable-next-line unicorn/no-null
              streetId: street?.id ?? null,
              municipalityId: city.id,
            });

            if (result.isError) {
              callback({
                type: "addressUpdateFail",
              });
            }

            callback({
              type: "addressUpdateSuccess",
            });
          };
        },
      },
      guards: {
        hasNoErrors: (context) => {
          return (
            context.cityError === undefined &&
            context.streetError === undefined &&
            context.houseNumberError === undefined &&
            context.apartmentNumberError === undefined
          );
        },
      },
    },
  );
};

export function useWWTMachine({
  onPositiveUpdate,
  onNegativeUpdate,
}: {
  onPositiveUpdate: () => void;
  onNegativeUpdate: () => void;
}) {
  return useMachine(
    React.useMemo(() => {
      return createWWTMachine({
        onPositiveUpdate,
        onNegativeUpdate,
      });
    }, [onNegativeUpdate, onPositiveUpdate]),
  );
}
